/**
 * The judge's answer in reading order: the difference at a glance, the bucket's reason, the
 * scoring, and what the agent covered that the AI did not. Shared by the simulator and the
 * bulk run. `confidenceScore` and the three suggestion lists are dead upstream and not drawn.
 */

import { useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Reveal } from '@/modules/simulator/ui/reveal';
import { CompareLegend, CriterionGaps, DimensionScore } from './score-compare';
import { Chip, ReportSection } from './report/report-bits';
import { SCORE_PARTS, missingPhrases, num, rec, str } from './verdict-readers';
import { ShadowComparison } from './shadow-comparison';

/** Past this many, the phrase list stops being a summary and becomes the page. */
const PHRASES_SHOWN = 6;

const MissingPhrases = ({ phrases }: { phrases: string[] }) => {
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? phrases : phrases.slice(0, PHRASES_SHOWN);
  const hidden = phrases.length - shown.length;

  return (
    <div className='flex flex-wrap gap-1.5'>
      {shown.map((phrase) => (
        <Chip key={phrase}>{phrase}</Chip>
      ))}
      {hidden > 0 && (
        <button
          type='button'
          onClick={() => setExpanded(true)}
          className='rounded-full border border-dashed border-gray-500 px-2 py-0.5 text-[11px] leading-4 text-gray-800 transition-colors hover:bg-gray-100'
        >
          +{hidden} more
        </button>
      )}
    </div>
  );
};

/** The bucket itself is not drawn here — the disclosure above already names it. */
export const JudgeVerdict = ({ comparison, aiReply, humanReply }: { comparison: Record<string, unknown>; aiReply?: string; humanReply?: string }) => {
  const [open, setOpen] = useState(false);

  const reason = str(comparison['bucketReason']);
  const aiTone = str(comparison['aiTone']);
  const humanTone = str(comparison['humanTone']);
  const missing = missingPhrases(comparison);
  const completenessAi = num(comparison['completenessScoreAi']);
  const completenessHuman = num(comparison['completenessScoreHuman']);
  const personalizationAi = num(comparison['personalizationScoreAi']);
  const personalizationHuman = num(comparison['personalizationScoreHuman']);
  const hasScores = [completenessAi, completenessHuman, personalizationAi, personalizationHuman].some((v) => v !== null);

  // toneMatch is forced false whenever the tone words differ, so the words replace the flag.
  const tones = aiTone && humanTone && aiTone !== humanTone ? `${aiTone} vs ${humanTone}` : null;
  const differentOutcome = comparison['actionMatch'] === false;

  return (
    <div className='flex flex-col gap-4 animate-in fade-in-0 duration-300 ease-out'>
      {(differentOutcome || tones) && (
        <div className='flex flex-wrap items-center gap-1.5'>
          {differentOutcome && <Chip tone='solid'>different outcome</Chip>}
          {tones && <Chip>tone · {tones}</Chip>}
        </div>
      )}

      {reason && <p className='text-[13px] leading-5 text-text-body'>{reason}</p>}

      <ShadowComparison comparison={comparison} aiReply={aiReply} humanReply={humanReply} />

      {hasScores && (
        <ReportSection title='Scoring' aside={<CompareLegend />}>
          <div className='flex flex-col gap-4'>
            <DimensionScore label='Completeness' ai={completenessAi} human={completenessHuman}>
              <Reveal open={open}>
                <div className='pt-2'>
                  <CriterionGaps parts={SCORE_PARTS.completeness} subScores={rec(comparison['completenessSubScores'])} />
                </div>
              </Reveal>
            </DimensionScore>

            <DimensionScore label='Personalization' ai={personalizationAi} human={personalizationHuman}>
              <Reveal open={open}>
                <div className='pt-2'>
                  <CriterionGaps
                    parts={SCORE_PARTS.personalization}
                    subScores={rec(comparison['personalizationSubScores'])}
                  />
                </div>
              </Reveal>
            </DimensionScore>
          </div>

          <button
            type='button'
            onClick={() => setOpen((prev) => !prev)}
            className='flex w-fit items-center gap-1 text-xs font-medium text-gray-800 transition-colors hover:text-text-heading'
          >
            <ChevronRight className={cn('size-3.5 transition-transform duration-300', open && 'rotate-90')} />
            {open ? 'Hide the breakdown' : 'Show the breakdown'}
          </button>
        </ReportSection>
      )}

      {missing.length > 0 && (
        <ReportSection
          title='The agent covered this, the AI did not'
          aside={<span className='text-[11px] text-gray-800 tabular-nums'>{missing.length}</span>}
        >
          <MissingPhrases phrases={missing} />
        </ReportSection>
      )}
    </div>
  );
};
